import React, { useEffect, useState } from "react";
import { useAuth, buildConsentRecord, POLICY_VERSION } from "../context/AuthContext";
import PrivacyDataModal from "./PrivacyDataModal";

const font = '"DM Sans", sans-serif';

// Only interrupt people inside the app itself, never on the legal pages
// (they must be able to read the documents before deciding).
const APP_PATHS = ["/", "/home", "/history", "/my-list", "/liked"];

// Asks signed-in users to accept the current Terms / Privacy Policy when:
//   - they signed up before consent was recorded, or
//   - the policy version has changed since they last accepted.
const PolicyUpdateGate = ({ path = window.location.pathname }) => {
  const { user, getUserData, updateUserData, logout } = useAuth();
  const [needed, setNeeded] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user || !APP_PATHS.includes(path)) {
      setNeeded(false);
      return undefined;
    }
    (async () => {
      try {
        const data = await getUserData();
        // No profile yet (brand-new signup still being created): nothing to ask.
        if (!data || cancelled) return;
        const upToDate =
          data.consent && data.consent.privacyVersion === POLICY_VERSION;
        if (!upToDate) setNeeded(true);
      } catch (e) {
        // If we cannot tell, do not lock the person out.
        console.error("Policy check failed:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, path]);

  if (showDelete) {
    return <PrivacyDataModal onClose={() => setShowDelete(false)} />;
  }
  if (!needed) return null;

  const accept = async () => {
    setSaving(true);
    setError("");
    try {
      await updateUserData({ consent: buildConsentRecord("reconsent") });
      setNeeded(false);
    } catch (e) {
      console.error(e);
      setError("Could not save your choice. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const btn = (primary) => ({
    padding: "0.75rem 1.2rem",
    borderRadius: 10,
    fontFamily: font,
    fontWeight: 700,
    fontSize: "0.88rem",
    cursor: "pointer",
    border: primary ? "none" : "2px solid #7c3aed",
    background: primary ? "#7c3aed" : "#fff",
    color: primary ? "#fff" : "#7c3aed",
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="We have updated our policies"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1900,
        background: "rgba(15,12,41,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        fontFamily: font,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "#fff",
          borderRadius: 18,
          padding: "1.4rem",
          boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
        }}
      >
        <h2 style={{ margin: "0 0 0.5rem", fontSize: "1.2rem", fontWeight: 800, color: "#1e1b4b" }}>
          We have updated our policies
        </h2>
        <p style={{ margin: "0 0 1rem", fontSize: "0.88rem", lineHeight: 1.6, color: "#4b5563" }}>
          NORYQ now has a new Privacy Policy, Terms of Service and Cookie Policy
          that follow Indian law. To keep using NORYQ, please confirm below.
        </p>

        <label
          style={{
            display: "flex",
            gap: "0.6rem",
            alignItems: "flex-start",
            fontSize: "0.85rem",
            lineHeight: 1.5,
            cursor: "pointer",
            background: "#f5f3ff",
            padding: "0.8rem",
            borderRadius: 12,
            marginBottom: "1rem",
            color: "#1e1b4b",
          }}
        >
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            style={{ marginTop: 3, width: 18, height: 18, flexShrink: 0 }}
          />
          <span>
            I am 18 years or older, and I have read and agree to the{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#7c3aed" }}>
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#7c3aed" }}>
              Privacy Policy
            </a>
            .
          </span>
        </label>

        {error && (
          <p role="alert" style={{ color: "#b91c1c", fontSize: "0.82rem", margin: "0 0 0.8rem" }}>
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
          <button
            type="button"
            style={{ ...btn(true), opacity: agreed && !saving ? 1 : 0.5, cursor: agreed && !saving ? "pointer" : "not-allowed" }}
            disabled={!agreed || saving}
            onClick={accept}
          >
            {saving ? "Saving..." : "Accept and continue"}
          </button>
          <button type="button" style={btn(false)} disabled={saving} onClick={() => logout()}>
            I do not agree, sign me out
          </button>
        </div>
        <p style={{ margin: "0.9rem 0 0", fontSize: "0.78rem", color: "#6b7280" }}>
          Prefer to leave?{" "}
          <button
            type="button"
            onClick={() => setShowDelete(true)}
            style={{ background: "none", border: "none", padding: 0, color: "#7c3aed", textDecoration: "underline", cursor: "pointer", font: "inherit" }}
          >
            Delete my account and data
          </button>
          .
        </p>
      </div>
    </div>
  );
};

export default PolicyUpdateGate;
